import { TextArea, Text, Flex } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { updateSkillRequiredInputs } from "../../../services/skill-documents";

const DEBOUNCE_MS = 500;

export interface RequiredInputsEditorProps {
  skillId: string;
  skillType: "builtin" | "custom";
  description: string;
  onDescriptionChange: (description: string) => void;
  disabled?: boolean;
}

export const RequiredInputsEditor = ({
  skillId,
  skillType,
  description,
  onDescriptionChange,
  disabled = false,
}: RequiredInputsEditorProps) => {
  const { t } = useTranslation("ui");
  const [localValue, setLocalValue] = useState(description);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocalValue(description);
  }, [description]);

  // Cancel any pending save on unmount so a stale skillId/skillType from a
  // previous selection can't fire after the user has switched skills.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      setLocalValue(val);
      onDescriptionChange(val);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          await updateSkillRequiredInputs({
            ...(skillType === "builtin"
              ? { skillId }
              : { workflowId: skillId }),
            requiredInputsDescription: val,
          });
        } catch {
          // fail silently; user can retry
        }
      }, DEBOUNCE_MS);
    },
    [skillId, skillType, onDescriptionChange],
  );

  return (
    <Flex direction="column" gap="2">
      <Text size="2" color="gray">
        {t("requiredInputsEditor.description")}
      </Text>
      <TextArea
        placeholder={t("requiredInputsEditor.placeholder")}
        value={localValue}
        onChange={handleChange}
        rows={3}
        disabled={disabled}
        data-testid={`required-inputs-editor-${skillId}`}
      />
    </Flex>
  );
};
